import {
  CRASH_FRICTION,
  CRASH_HARSHNESS,
  CRASH_RESTITUTION,
  CRASH_RESTITUTION_MIN,
  CRASH_SPEED_REF,
  MIN_SPEED,
} from './constants.ts';
import { add, bounceDirection, clamp, dot, length, scale, toAngle } from './math.ts';
import type { BoatState, Vec2 } from './types.ts';

/** Clipped travel and shore angle for a solid hit, before harshness is applied. */
export type CrashImpact = {
  impact: number;
  crossingDeg: number;
};

/** Where the hull leaves a solid hit, and how far it still travels this turn. */
export type CrashVelocity = {
  heading: number;
  speed: number;
};

/**
 * Fraction of along-shore speed kept before harshness. A vanishing hit matches
 * `1 - CRASH_FRICTION`. A faster hit crumples that keep toward 0 on the same
 * `CRASH_SPEED_REF` curve the head-on uses, so a race-speed scrape does not
 * leave the wall still fast.
 */
export function shoreKeepFraction(impact: number): number {
  const slow = 1 - CRASH_FRICTION;
  const crumple = 1 + (Math.max(0, impact) / CRASH_SPEED_REF) ** 2;

  return slow / crumple;
}

/**
 * Speed after a solid overwater hit. Impact is the clipped distance just swum.
 * `crossingDeg` is 0 at a graze and 90 head-on. `harshness` scales the designed
 * loss (0 keeps impact; 1 is the authored curve).
 */
export function crashOutgoingSpeed(
  impact: number,
  crossingDeg: number,
  harshness: number = CRASH_HARSHNESS,
): number {
  if (impact <= MIN_SPEED) {
    return MIN_SPEED;
  }

  const theta = (clamp(crossingDeg, 0, 90) * Math.PI) / 180;

  return spendLoss(
    impact,
    keptSpeed(impact * Math.sin(theta), impact * Math.cos(theta), impact),
    harshness,
  );
}

/**
 * Heading and speed the hull leaves a solid hit with, from the same split the
 * speed loss uses: the shore normal returns `restitution` of the normal speed,
 * and the along-shore run keeps `shoreKeepFraction` of its speed. A slow graze
 * still runs along the wall instead of mirroring back out to sea. A fast one
 * sheds that run. A head-on is a near-stop rather than a full-speed reversal
 * down its own track.
 */
export function crashOutgoingVelocity(
  approach: Vec2,
  edge: Vec2,
  impact: number,
  harshness: number = CRASH_HARSHNESS,
): CrashVelocity | null {
  const approachLen = length(approach);
  const edgeLen = length(edge);

  if (approachLen < 1e-9 || edgeLen < 1e-9) {
    return null;
  }

  const travel = scale(approach, 1 / approachLen);
  const alongShore = scale(edge, 1 / edgeLen);
  const outward = waterNormal(travel, alongShore);
  const normalSpeed = Math.abs(dot(travel, outward)) * impact;
  const shoreSpeed = dot(travel, alongShore) * impact;
  const alongKeep = shoreKeepFraction(impact);
  const outgoing = withoutNegativeZero(
    add(
      scale(outward, restitutionAt(normalSpeed) * normalSpeed),
      scale(alongShore, alongKeep * shoreSpeed),
    ),
  );
  const mirrored = bounceDirection(approach, edge);

  return {
    heading: length(outgoing) > 1e-9
      ? toAngle(outgoing)
      : toAngle(withoutNegativeZero(mirrored ?? scale(travel, -1))),
    speed:
      impact <= MIN_SPEED
        ? MIN_SPEED
        : spendLoss(impact, keptSpeed(normalSpeed, Math.abs(shoreSpeed), impact), harshness),
  };
}

/** Re-apply crash loss to a landed boat when the debug harshness slider moves. */
export function applyCrashHarshness(
  boat: BoatState,
  crash: CrashImpact,
  harshness: number = CRASH_HARSHNESS,
): BoatState {
  return {
    ...boat,
    speed: crashOutgoingSpeed(crash.impact, crash.crossingDeg, harshness),
  };
}

/** A dead-astern bounce must read as `+π`, not the `-π` a signed zero would give. */
function withoutNegativeZero(vector: Vec2): Vec2 {
  return { x: vector.x === 0 ? 0 : vector.x, y: vector.y === 0 ? 0 : vector.y };
}

/** The edge perpendicular that faces back along the incoming path, so into water. */
function waterNormal(travel: Vec2, alongShore: Vec2): Vec2 {
  const candidate = { x: -alongShore.y, y: alongShore.x };

  return dot(candidate, travel) > 0
    ? { x: -candidate.x, y: -candidate.y }
    : candidate;
}

/** Faster rams crumple: the normal keep falls from `CRASH_RESTITUTION` toward the floor. */
export function restitutionAt(normalSpeed: number): number {
  const crumple = 1 + (normalSpeed / CRASH_SPEED_REF) ** 2;

  return CRASH_RESTITUTION_MIN + (CRASH_RESTITUTION - CRASH_RESTITUTION_MIN) / crumple;
}

function keptSpeed(normalSpeed: number, shoreSpeed: number, impact: number): number {
  return Math.hypot(
    restitutionAt(normalSpeed) * normalSpeed,
    shoreKeepFraction(impact) * shoreSpeed,
  );
}

function spendLoss(impact: number, kept: number, harshness: number): number {
  if (harshness <= 0) {
    return impact;
  }

  return Math.max(MIN_SPEED, impact - (impact - kept) * harshness);
}
